import { useId, type ReactNode } from 'react';
import { Chip } from '../../components/Chip';
import { Icon } from '../../components/Icon';
import { countLabel } from '../../lib/format';
import type { ImportDecision } from '../../lib/types';
import StammIcon from '../stammdaten/StammIcon';
import { PlanDecision } from './PlanDecision';
import type { BomGroup } from './planPresentation';

interface BomReviewProps {
  groups: readonly BomGroup[];
  decisions: Readonly<Record<string, ImportDecision>>;
  showUnchanged: boolean;
  disabled: boolean;
  onDecide: (entryId: string, decision: ImportDecision) => void;
}

function BomCard({ group, decisions, showUnchanged, disabled, onDecide }: Omit<BomReviewProps, 'groups'> & { group: BomGroup }): ReactNode {
  const headingId = useId();
  const visible = group.components.filter(({ entry }) => showUnchanged || entry.action !== 'unchanged');
  if (visible.length === 0) return null;
  const hiddenCount = group.components.length - visible.length;
  const productLabel = `${group.productName}${group.productVersion ? ` · Version ${group.productVersion}` : ''}`;

  return (
    <article className="imp-bom" aria-labelledby={headingId}>
      <header className="imp-bom-head">
        <span className="imp-bom-icon"><StammIcon name="box" /></span>
        <div className="imp-bom-product">
          <div className="imp-bom-title">
            <h3 id={headingId}>{group.productName}</h3>
            {group.productVersion ? <Chip className="imp-version">Version {group.productVersion}</Chip> : null}
          </div>
        </div>
        <span className="imp-bom-count">{countLabel(group.components.length, 'Komponente', 'Komponenten')}</span>
      </header>
      <table className="imp-bom-table" role="table">
        <caption className="visually-hidden">Importvorschau: Stückliste für {productLabel}</caption>
        <thead role="rowgroup">
          <tr role="row">
            <th scope="col" role="columnheader">Komponente</th>
            <th scope="col" role="columnheader">Version</th>
            <th scope="col" role="columnheader">Änderung</th>
            <th scope="col" role="columnheader" className="imp-bom-choice">Import</th>
          </tr>
        </thead>
        <tbody role="rowgroup">
          {visible.map(({ entry, componentName, componentVersion, changes }) => {
            const decision = decisions[entry.entry_id] ?? null;
            const skipped = decision === 'existing';
            const status = skipped ? 'Übersprungen' : entry.action === 'create' ? 'Neu in Stückliste' : entry.action === 'update' ? 'Geändert' : 'Unverändert';
            return (
              <tr key={entry.entry_id} role="row" className={skipped ? 'imp-bom-skipped' : undefined}>
                <th scope="row" role="rowheader" className="imp-bom-component">
                  <StammIcon name="component" />
                  <span>{componentName}</span>
                  {changes.length > 0 ? (
                    <dl className="imp-bom-changes">
                      {changes.map((change, index) => (
                        <div key={`${change.field}-${index}`}>
                          <dt>{change.field}</dt>
                          <dd>
                            {change.old !== null ? <><span className="visually-hidden">Bisher: </span><del>{change.old}</del> <Icon name="arrow-right" size={12} /> <span className="visually-hidden">Neu: </span></> : null}
                            {change.new}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  ) : null}
                </th>
                <td role="cell" className="imp-bom-version">
                  <span className="imp-mobile-label" aria-hidden="true">Version</span>
                  <span>{componentVersion || 'Nicht angegeben'}</span>
                </td>
                <td role="cell" className="imp-bom-status">
                  <Chip tone={skipped || entry.action === 'unchanged' ? 'neutral' : entry.action === 'create' ? 'good' : 'warn'}>{status}</Chip>
                </td>
                <td role="cell" className="imp-bom-choice">
                  <PlanDecision
                    entry={entry}
                    label={`${componentName}${componentVersion ? ` ${componentVersion}` : ''} in ${productLabel}`}
                    decision={decision}
                    disabled={disabled}
                    onDecide={onDecide}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {hiddenCount > 0 ? (
        <p className="imp-bom-note">{countLabel(hiddenCount, 'unveränderte Komponente ausgeblendet', 'unveränderte Komponenten ausgeblendet')}.</p>
      ) : null}
    </article>
  );
}

export function BomReview({ groups, ...props }: BomReviewProps): ReactNode {
  return groups.map((group) => <BomCard key={group.key} group={group} {...props} />);
}
